import React from 'react';
import { Phone, Calendar, Camera, Eye, Mountain } from 'lucide-react';

const Tarangire = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">Tarangire National Park</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Main Title */}
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          Tarangire National Park - Tarangire Safari Tours
        </h2>

        {/* Main Description */}
        <div className="mb-8">
          <p className="text-gray-700 leading-relaxed mb-4">
            Tarangire National Park forms the centerpiece of an annual migratory cycle that includes up to 3,000 elephants, 25,000 wildebeest, and 30,000 zebra. It is a significant destination for safari enthusiasts. The park derives its name from the Tarangire River, which meanders its landscape and serves as a crucial water source for animals, particularly during the dry season.
          </p>
          
          <p className="text-gray-700 leading-relaxed">
            Situated between the meadows of Tarangire and south of Lake Manyara National Park and a short drive from Arusha, Tarangire is conveniently located for visitors traversing the Northern Tanzania safari circuit, including routes to Ngorongoro and the Serengeti. For those seeking both luxury and adventure, the park offers various high-end safari lodges.
          </p>
        </div>

        {/* Wildlife and Scenery Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4 flex items-center">
            <Camera className="mr-2" size={24} />
            Wildlife and Scenery
          </h3>
          <p className="text-gray-700 leading-relaxed">
            Tarangire is famed for its large elephant populations, especially during the peak migration from July to October when up to 3,000 gather in a single day. The park provides habitat for giraffes, ostriches, and over 550 bird species at once. Besides elephants, the park hosts a variety of other mammals such as lions, zebras, and antelopes. The park's vast grasslands and iconic baobab trees provide a rugged and distinct "Out of Africa" atmosphere.
          </p>
        </div>

        {/* Best Time to Visit Section */}
        <div className="mb-8 p-6 border border-[#9acd32]/20 rounded-lg">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4 flex items-center">
            <Calendar className="mr-2" size={24} />
            Best Time to Visit Tarangire National Park
          </h3>
          <p className="text-gray-700 leading-relaxed">
            The best time to visit Tarangire is during the dry season from June to October. During these months, wildlife congregates around the Tarangire River, making game viewing easier and more rewarding with increased animal density and migratory patterns. To observe large herds of animals, particularly elephants, visit between July and October. For bird watchers, the wet season from November to May offers optimal conditions as it is outside peak season but still relatively dry. The low season from late November to May is ideal for bird watching, as the park becomes lush and green.
          </p>
        </div>

        {/* Contact Information */}
        <div className="mb-8 p-6 border border-[#9acd32]/20 rounded-lg">
          <div className="text-center">
            <p className="text-[#2f4f2f] font-semibold mb-2">Ready to Plan a Tanzania Safari? Talk to a Travel Adviser Now (Call/WhatsApp)</p>
            <div className="flex items-center justify-center text-[#2f4f2f] font-bold text-xl">
              <Phone className="mr-2" size={20} />
              <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
            </div>
          </div>
        </div>

        {/* Climate Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4">Climate</h3>
          <p className="text-gray-700 leading-relaxed">
            The climate in Tarangire is generally pleasant and temperate. The park experiences "long rains" from March to May and "short rains" from November to December, typically in the afternoon rather than throughout the day. The dry season runs from June to October with clear skies and mild temperatures, with afternoon highs around 25°C (77°F) and cooler nights at approximately 16°C (61°F). Warm clothing is recommended for early morning game drives.
          </p>
        </div>

        {/* Activities Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-4 flex items-center">
            <Eye className="mr-2" size={24} />
            Activities and Attractions in Tarangire National Park
          </h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-lg font-semibold text-[#2f4f2f] mb-2 flex items-center">
                <Mountain className="mr-2" size={16} />
                Game Drives and Walking Tours
              </h4>
              <p className="text-gray-700 leading-relaxed ml-6">
                Tarangire offers a variety of wildlife viewing opportunities, including game drives and walking tours and fly camping safaris.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-[#2f4f2f] mb-2">Night Game Drives</h4>
              <p className="text-gray-700 leading-relaxed ml-6">
                Night game drives are permitted in the park but only through select accommodations such as Swala and Oliver's Camp, which have Tabaka's requirements.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-[#2f4f2f] mb-2">Balloon Safaris</h4>
              <p className="text-gray-700 leading-relaxed ml-6">
                Hot air balloon safaris provide an exceptional aerial view of the park's wildlife and scenery, including lions and other carnivores.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-[#2f4f2f] mb-2">Bird Watching</h4>
              <p className="text-gray-700 leading-relaxed ml-6">
                With over 500 bird species, Tarangire is a paradise for bird enthusiasts. The best birding period is from February to March during the wet season, offering an enriching experience for avian lovers.
              </p>
            </div>
          </div>

          <p className="text-gray-700 leading-relaxed mt-4">
            Tarangire National Park stands out as a premier <a href="/destinations" className="text-[#9acd32] underline hover:text-[#228b22] transition-colors">safari destination in Tanzania</a>, offering <a href="/wildlife" className="text-[#9acd32] underline hover:text-[#228b22] transition-colors">diverse wildlife</a>, stunning landscapes, and unique activities for visitors.
          </p>
        </div>

        {/* Safari Packages Section */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">Our Top Tanzania Safari Packages to Tarangire National Park</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Package 1 */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="/15.jpg" 
                alt="3 Days Tanzania Luxury Safari" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <div className="bg-[#9acd32] text-[#2f4f2f] px-3 py-1 rounded text-sm inline-block mb-2">
                  3 Days Tanzania Luxury Safari
                </div>
              </div>
            </div>

            {/* Package 2 */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img 
                src="/2.jpg" 
                alt="5 Days Best of Tanzania Safari" 
                className="w-full h-48 object-cover"
              />
              <div className="p-4">
                <div className="bg-[#9acd32] text-[#2f4f2f] px-3 py-1 rounded text-sm inline-block mb-2">
                  5 Days Best of Tanzania Safari
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tarangire;